import { routesEnum } from 'src/shared/const';

type RuntimeConfigKey =
  | 'REACT_APP_AUTH_URL'
  | 'REACT_APP_PROJECTS_URL'
  | 'REACT_APP_PROJECT_URL'
  | 'REACT_APP_TABLE_SOCKET_URL'
  | 'REACT_APP_BASENAME'
  | 'REACT_APP_OIDC_FASTBOARD_AUTH_URL'
  | 'REACT_APP_OIDC_FASTBOARD_CLIENT_ID'
  | 'REACT_APP_OIDC_FASTBOARD_SCOPE'
  | 'REACT_APP_OIDC_FASTBOARD_RESPONSE_TYPE'
  | 'REACT_APP_OIDC_FASTBOARD_REDIRECT_URI';

type RuntimeConfig = Partial<Record<RuntimeConfigKey, string>>;

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeConfig;
  }
}

const runtimeConfig: RuntimeConfig =
  typeof window === 'undefined' ? {} : window.__APP_CONFIG__ || {};

const trim = (value?: string) => value?.trim() ?? '';

const normalizeBasename = (value?: string) => {
  const normalized = trim(value);

  if (!normalized || normalized === '/') {
    return '';
  }

  const leadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
  return leadingSlash.replace(/\/+$/, '');
};

const normalizeAppPath = (path: string) => {
  if (!path) {
    return '';
  }

  return path.startsWith('/') ? path : `/${path}`;
};

const detectAppBasename = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const knownRouteSegments = new Set(
    Object.values(routesEnum)
      .map(route => route.split('/').filter(Boolean)[0])
      .filter(Boolean),
  );

  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
  return firstSegment && !knownRouteSegments.has(firstSegment) ? `/${firstSegment}` : '';
};

const getConfigValue = (key: RuntimeConfigKey, fallback = '') =>
  trim(runtimeConfig[key]) || trim(process.env[key]) || fallback;

export const isDevApi = process.env.REACT_APP_BUILD === 'development';
export const APP_BASENAME = normalizeBasename(
  getConfigValue('REACT_APP_BASENAME', detectAppBasename()),
);

const legacyAuthUrl = isDevApi
  ? 'https://dis-back-dev.bots.winsolutions.ru/'
  : 'https://dis-back-stage.drawbot.winsolutions.ru/';

const legacyProjectsUrl = isDevApi
  ? 'https://dis-back2-dev.bots.winsolutions.ru/'
  : 'https://dis-back2-stage.drawbot.winsolutions.ru/';

const legacyProjectUrl = isDevApi
  ? 'https://dis-back3-dev.bots.winsolutions.ru/'
  : 'https://dis-back3-stage.drawbot.winsolutions.ru/';

const legacyTableSocketUrl = isDevApi
  ? 'wss://dis-back2-dev.bots.winsolutions.ru/ws'
  : 'wss://dis-back2-stage.drawbot.winsolutions.ru/ws';

export const AUTH_URL = getConfigValue('REACT_APP_AUTH_URL', legacyAuthUrl);

export const PROJECTS_URL = getConfigValue('REACT_APP_PROJECTS_URL', legacyProjectsUrl);

export const PROJECT_URL = getConfigValue('REACT_APP_PROJECT_URL', legacyProjectUrl);

export const TABLE_SOCKET_URL = getConfigValue(
  'REACT_APP_TABLE_SOCKET_URL',
  legacyTableSocketUrl,
);

export const OIDC_FASTBOARD_AUTH_URL = getConfigValue(
  'REACT_APP_OIDC_FASTBOARD_AUTH_URL',
  isDevApi ? 'https://fastboard-front-dev4.fb-dev.winsolutions.ru/back/api/oidc/auth' : '',
);

export const OIDC_FASTBOARD_CLIENT_ID = getConfigValue(
  'REACT_APP_OIDC_FASTBOARD_CLIENT_ID',
  isDevApi ? 'f768be1d-6394-4f3b-a839-8a7516e0e731' : '',
);

export const OIDC_FASTBOARD_SCOPE = getConfigValue(
  'REACT_APP_OIDC_FASTBOARD_SCOPE',
  'openid profile email',
);

export const OIDC_FASTBOARD_RESPONSE_TYPE = getConfigValue(
  'REACT_APP_OIDC_FASTBOARD_RESPONSE_TYPE',
  'code',
);

export const OIDC_FASTBOARD_REDIRECT_URI = getConfigValue(
  'REACT_APP_OIDC_FASTBOARD_REDIRECT_URI',
);

export const withAppBasename = (path = '') => {
  const normalizedPath = normalizeAppPath(path);
  return `${APP_BASENAME}${normalizedPath}` || '/';
};

export const buildAbsoluteAppUrl = (path = '') => {
  const fullPath = withAppBasename(path);

  if (typeof window === 'undefined') {
    return fullPath;
  }

  return `${window.location.origin}${fullPath}`;
};
